import React, { useState, useEffect, useCallback } from 'react'
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import { db } from 'common/config/Firebase'
import { TEST_ICON_MAP } from 'common/config/testIconsConfig'

const ResultIcon = ({ value, size = 22 }) => {
    if (value === true) return <CheckCircleIcon style={{ fontSize: size, color: '#43a047' }} />
    if (value === false) return <CancelIcon style={{ fontSize: size, color: '#e53935' }} />
    return <RadioButtonUncheckedIcon style={{ fontSize: size, color: '#ccc' }} />
}

const TestIcon = ({ iconName, size = 24 }) => {
    const Icon = TEST_ICON_MAP[iconName]
    if (!Icon) return null
    return <Icon style={{ fontSize: size }} />
}

const TestsDialog = ({ open, onClose, ticketId, initialResults, collection = 'tasks' }) => {
    const [testItems, setTestItems] = useState([])
    const [results, setResults] = useState({})
    const [mode, setMode] = useState('manual')
    const [autoIndex, setAutoIndex] = useState(0)
    const [autoFinished, setAutoFinished] = useState(false)

    useEffect(() => {
        const unsub = db.collection('meta').doc('settings').onSnapshot(snap => {
            setTestItems((snap.exists ? snap.data().testItems : null) || [])
        })
        return () => unsub()
    }, [])

    useEffect(() => {
        if (open) {
            const init = {}
            const raw = initialResults || {}
            Object.keys(raw).forEach(k => { init[String(k)] = raw[k] })
            setResults(init)
            setMode('manual')
            setAutoIndex(0)
            setAutoFinished(false)
        }
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    const save = useCallback((newResults) => {
        if (!ticketId) return
        db.collection(collection).doc(ticketId).update({ testResults: newResults })
    }, [ticketId, collection])

    const markResult = (index, value) => {
        const key = String(index)
        setResults(prev => {
            const next = { ...prev }
            if (next[key] === value) delete next[key]
            else next[key] = value
            save(next)
            return next
        })
    }

    const markAndAdvance = useCallback((value) => {
        const key = String(autoIndex)
        setResults(prev => {
            const next = { ...prev, [key]: value }
            save(next)
            return next
        })
        if (autoIndex + 1 >= testItems.length) {
            setAutoFinished(true)
        } else {
            setAutoIndex(i => i + 1)
        }
    }, [autoIndex, testItems.length, save])

    useEffect(() => {
        if (!open || mode !== 'auto' || autoFinished) return
        const handler = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                markAndAdvance(true)
            } else if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                markAndAdvance(false)
            }
        }
        document.addEventListener('keydown', handler, true)
        return () => document.removeEventListener('keydown', handler, true)
    }, [open, mode, autoFinished, markAndAdvance])

    const startAuto = () => {
        setAutoIndex(0)
        setAutoFinished(false)
        setMode('auto')
    }

    const currentItem = testItems[autoIndex]

    const hasResults = testItems.length > 0 && Object.keys(results).length > 0
    const allOk = hasResults && testItems.every((_, i) => results[String(i)] === true)
    const issues = hasResults ? testItems.filter((_, i) => results[String(i)] !== true) : []

    const renderSummaryBanner = () => {
        if (!hasResults) return null
        if (allOk) return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#e8f5e9', borderRadius: 8, padding: '14px 16px', marginBottom: 16,
            }}>
                <CheckCircleIcon style={{ fontSize: 28, color: '#43a047' }} />
                <span style={{ fontWeight: 700, fontSize: 17, color: '#2e7d32' }}>
                    Test realizado - Todo OK
                </span>
            </div>
        )
        return (
            <div style={{ background: '#fff3e0', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
                    <CancelIcon style={{ fontSize: 26, color: '#e65100' }} />
                    <span style={{ fontWeight: 700, fontSize: 17, color: '#bf360c' }}>
                        Test realizado - Fallos / no comprobables:
                    </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                    {issues.map((item, idx) => {
                        const itemIndex = testItems.indexOf(item)
                        const isFail = results[String(itemIndex)] === false
                        return (
                            <span key={idx} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: isFail ? '#ffebee' : '#f5f5f5',
                                border: `1px solid ${isFail ? '#ef9a9a' : '#ccc'}`,
                                borderRadius: 4, padding: '3px 8px', fontSize: 13,
                            }}>
                                <TestIcon iconName={item.icon} size={14} />
                                {item.name}
                            </span>
                        )
                    })}
                </div>
            </div>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={mode === 'auto' && !autoFinished}
        >
            <DialogTitle style={{ textAlign: 'center' }}>
                Test de reparación
                <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {mode === 'manual' && (
                    <div>
                        {renderSummaryBanner()}
                        {testItems.length === 0 && (
                            <p style={{ color: '#888', textAlign: 'center', padding: '24px 0' }}>
                                No hay tests configurados. Añádelos en Configuración → Reparaciones.
                            </p>
                        )}
                        {testItems.map((item, i) => {
                            const val = results[String(i)]
                            return (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 8px',
                                        borderBottom: '1px solid #f0f0f0',
                                        gap: 10,
                                    }}
                                >
                                    <span style={{ width: 28, display: 'flex', justifyContent: 'center', color: '#555' }}>
                                        <TestIcon iconName={item.icon} size={22} />
                                    </span>
                                    <span style={{ flex: 1, fontSize: 15 }}>{item.name}</span>
                                    <span
                                        onClick={() => markResult(i, true)}
                                        style={{
                                            width: 38, height: 38,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            background: val === true ? '#e8f5e9' : 'transparent',
                                            opacity: val === false ? 0.25 : 1,
                                        }}
                                    >
                                        <CheckCircleIcon style={{ fontSize: 26, color: '#43a047' }} />
                                    </span>
                                    <span
                                        onClick={() => markResult(i, false)}
                                        style={{
                                            width: 38, height: 38,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            background: val === false ? '#ffebee' : 'transparent',
                                            opacity: val === true ? 0.25 : 1,
                                        }}
                                    >
                                        <CancelIcon style={{ fontSize: 26, color: '#e53935' }} />
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                )}

                {mode === 'auto' && !autoFinished && currentItem && (
                    <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                        <div style={{ color: '#333', marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                            <TestIcon iconName={currentItem.icon} size={90} />
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 'bold', marginBottom: 40 }}>
                            {currentItem.name}
                        </div>
                        <div style={{ color: '#888', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                            <span>{autoIndex + 1} / {testItems.length}</span>
                            <span>·</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <strong>Enter / Espacio</strong> = <CheckCircleIcon style={{ fontSize: 18, color: '#43a047' }} />
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <strong>Esc</strong> = <CancelIcon style={{ fontSize: 18, color: '#e53935' }} />
                            </span>
                        </div>
                    </div>
                )}

                {mode === 'auto' && autoFinished && (
                    <div>
                        {renderSummaryBanner()}
                        {testItems.map((item, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 8px',
                                        borderBottom: '1px solid #f0f0f0',
                                        gap: 12,
                                    }}
                                >
                                    <span style={{ width: 28, display: 'flex', justifyContent: 'center', color: '#555' }}>
                                        <TestIcon iconName={item.icon} size={22} />
                                    </span>
                                    <span style={{ flex: 1, fontSize: 15 }}>{item.name}</span>
                                    <ResultIcon value={results[String(i)]} />
                                </div>
                            ))}
                    </div>
                )}
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
                {mode === 'manual' ? (
                    <Button
                        onClick={startAuto}
                        color="primary"
                        variant="contained"
                        disabled={testItems.length === 0}
                    >
                        Modo automático ▶
                    </Button>
                ) : (
                    <Button onClick={() => setMode('manual')}>
                        Modo manual
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default TestsDialog
